/**
 * PaymentIntent — a unique reference / control number issued before payment
 * is initiated. The customer or dealer creates an intent; the payment
 * provider's webhook later matches a real payment to it.
 *
 * Status machine:
 *   pending → matched   (provider webhook matched it; Payment row created)
 *   pending → expired   (TTL elapsed without a match — typically 60 min)
 *   pending → cancelled (user-initiated cancel)
 *
 * Matched / expired / cancelled are terminal.
 */
import { z } from "zod";
import { err, ok } from "../result.js";
const TERMINAL_INTENT_STATES = new Set([
    "matched",
    "expired",
    "cancelled",
]);
const moneySchema = z.object({
    amountMinor: z.number().int().positive(),
    currency: z.string().length(3),
});
export const paymentIntentSchema = z.object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    loanId: z.string().uuid(),
    amount: moneySchema,
    channel: z.string().min(1),
    reference: z.string().min(1),
    status: z.enum(["pending", "matched", "expired", "cancelled"]),
    expiresAt: z.coerce.date(),
    matchedPaymentId: z.string().uuid().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export function makePaymentIntent(raw) {
    const parsed = paymentIntentSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        loanId: parsed.loanId,
        amount: {
            amountMinor: parsed.amount.amountMinor,
            currency: parsed.amount.currency,
        },
        channel: parsed.channel,
        reference: parsed.reference,
        status: parsed.status,
        expiresAt: parsed.expiresAt,
        ...(parsed.matchedPaymentId !== undefined
            ? { matchedPaymentId: parsed.matchedPaymentId }
            : {}),
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
const MIN_REASON_LEN = 10;
export function transitionPaymentIntent(intent, event, now = new Date()) {
    if (TERMINAL_INTENT_STATES.has(intent.status)) {
        return err({ kind: "TerminalState", current: intent.status });
    }
    switch (event.kind) {
        case "Match": {
            // Reject if the intent has elapsed past `expiresAt` — the provider
            // webhook is past the window and the reconciliation worker should
            // have already auto-expired this intent.
            if (intent.expiresAt.getTime() < now.getTime()) {
                return err({ kind: "AlreadyExpired" });
            }
            return ok({
                ...intent,
                status: "matched",
                matchedPaymentId: event.paymentId,
                updatedAt: event.at,
            });
        }
        case "Expire":
            return ok({ ...intent, status: "expired", updatedAt: event.at });
        case "Cancel":
            if (event.reason.length < MIN_REASON_LEN) {
                return err({ kind: "ReasonTooShort", minLength: MIN_REASON_LEN });
            }
            return ok({ ...intent, status: "cancelled", updatedAt: event.at });
    }
}
//# sourceMappingURL=payment-intent.js.map